import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Badge, Button, Stat, showToast } from '../components/UI';

export default function SellerDashboard() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOffers, setExpandedOffers] = useState({});
  const [offerData, setOfferData] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    api.getMyListings()
      .then(setListings)
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  const toggleOffers = async (listingId) => {
    if (expandedOffers[listingId]) {
      setExpandedOffers(prev => ({ ...prev, [listingId]: false }));
      return;
    }
    try {
      const offers = await api.getListingOffers(listingId);
      setOfferData(prev => ({ ...prev, [listingId]: offers }));
      setExpandedOffers(prev => ({ ...prev, [listingId]: true }));
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleConfirmPayment = async (listingId) => {
    try {
      await api.confirmPayment(listingId);
      showToast('Payment confirmed!');
      const updated = await api.getMyListings();
      setListings(updated);
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const active = listings.filter(l => l.status === 'active').length;
  const totalOffers = listings.reduce((sum, l) => sum + (l.totalOffers || 0), 0);
  const awarded = listings.filter(l => l.status === 'awarded' || l.status === 'paid' || l.status === 'shipped' || l.status === 'completed').length;

  return (
    <div style={{ padding: 32, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h2 style={{ fontSize: 28, fontWeight: 800, color: 'var(--text)', fontFamily: 'var(--sans)', letterSpacing: '-0.02em', marginBottom: 8 }}>Seller Dashboard</h2>
          <p style={{ fontSize: 14, color: 'var(--text3)' }}>Manage listings, track offers, and close auctions.</p>
        </div>
        <Button onClick={() => navigate('/create-listing')}>+ New Listing</Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 32 }}>
        <Stat label="Total Listings" value={listings.length} color="var(--text)" />
        <Stat label="Active" value={active} color="var(--accent)" />
        <Stat label="Total Offers" value={totalOffers} color="var(--blue)" />
        <Stat label="Awarded/Sold" value={awarded} color="var(--amber)" />
      </div>

      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border)', fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>My Listings</div>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>Loading...</div>
        ) : listings.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>
            No listings yet. <span style={{ color: 'var(--accent)', cursor: 'pointer' }} onClick={() => navigate('/create-listing')}>Create your first listing →</span>
          </div>
        ) : (
          <div>
            {listings.map(l => {
              const endsAt = new Date(l.auctionEndsAt);
              const msLeft = endsAt - new Date();
              const isActive = l.status === 'active' && msLeft > 0;

              return (
                <div key={l.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', flexWrap: 'wrap', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>{l.title}</div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <Badge color={isActive ? 'green' : l.status === 'awarded' ? 'amber' : 'neutral'} small>
                          {isActive ? 'LIVE' : l.status?.toUpperCase()}
                        </Badge>
                        <span style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{l.totalOffers} offers</span>
                      </div>
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--accent)', fontFamily: 'var(--sans)', minWidth: 100, textAlign: 'right' }}>
                      ${Number(l.currentHighestBid || l.startingPrice).toLocaleString()}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {l.totalOffers > 0 && (
                        <Button small variant="secondary" onClick={() => toggleOffers(l.id)}>
                          {expandedOffers[l.id] ? 'Hide Offers' : 'View Offers'}
                        </Button>
                      )}
                      {l.status === 'awarded' && (
                        <Button small onClick={() => handleConfirmPayment(l.id)}>Confirm Payment</Button>
                      )}
                    </div>
                  </div>

                  {/* Expanded offers */}
                  {expandedOffers[l.id] && offerData[l.id] && (
                    <div style={{ padding: '0 24px 16px', background: 'var(--bg2)' }}>
                      <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', padding: '8px 0', marginBottom: 4 }}>
                        Top 3 offers (buyer identities hidden)
                      </div>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr>
                            {['Buyer', 'Amount', 'Status', 'Date'].map(h => (
                              <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', fontWeight: 500, borderBottom: '1px solid var(--border)' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {offerData[l.id].map(o => (
                            <tr key={o.id} style={{ borderBottom: '1px solid var(--border)' }}>
                              <td style={{ padding: '10px 16px', color: 'var(--text2)', fontWeight: 600 }}>{o.buyerLabel || 'Anonymous'}</td>
                              <td style={{ padding: '10px 16px', fontFamily: 'var(--mono)', color: 'var(--accent)', fontWeight: 600 }}>${Number(o.amount).toLocaleString()}</td>
                              <td style={{ padding: '10px 16px' }}>
                                <Badge color={o.status === 'active' ? 'green' : o.status === 'won' ? 'blue' : 'neutral'} small>{o.status}</Badge>
                              </td>
                              <td style={{ padding: '10px 16px', fontFamily: 'var(--mono)', color: 'var(--text3)', fontSize: 12 }}>{new Date(o.createdAt).toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
